import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';

import { allPremisesKey } from 'app/premises/hooks/usePremisesList';
import { GetPropertyConnectionResponse, LinkData } from 'app/connection/api';
import { generateNodeId } from 'common/offline/generateNodeId';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { propertyConnectionKey } from 'app/areas/hooks/useGetPropertyConnections';
import { CREATE_FIBER, MutateCreateFiberFiber } from '../outlets/api';

// Type for fiber with generated ID (for offline support)
type FiberWithId = Omit<MutateCreateFiberFiber, 'id'> & { id: string };

export const useCreateFiber = (propertyId: null | number) => {
  const queryClient = useQueryClient();
  const cacheKey = propertyConnectionKey(propertyId);

  const { mutate, mutateAsync, isLoading, isError, isSuccess, data } = useOfflineMutation<
    string,
    unknown,
    MutateCreateFiberFiber
  >({
    mutationFn: async (fiber) =>
      requestGQL(CREATE_FIBER, {
        propertyId,
        fiber,
      }),
    mutationName: 'createFiber',
    entity: 'fiber',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId], allPremisesKey(propertyId)],

    // Prepare payload for offline queue - ensure it has an ID
    getOptimisticData: (fiber) => {
      const fiberWithId: FiberWithId = {
        ...fiber,
        id: fiber.id ?? generateNodeId(),
      };
      return fiberWithId;
    },

    // Apply optimistic update to connection cache - add link to linkDataArray
    applyOptimisticUpdate: (optimisticData) => {
      const fiber = optimisticData as FiberWithId;

      queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(cacheKey, (oldData) => {
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
          return oldData;
        }

        const existingLinks = oldData.propertyById.connectionByConnectionId.serialized2.linkDataArray ?? [];

        // Create optimistic link data matching LinkData type
        const newLink: LinkData = {
          id: fiber.id,
          from: fiber.odfId,
          to: fiber.outletId,
          fromPort: fiber.odfPort,
          toPort: fiber.outletPort,
          text: null,
          highlight: false,
          type: 'Fiber',
          cfObject: {
            duct: fiber.duct ?? '',
            length: fiber.length && !Number.isNaN(fiber.length) ? fiber.length : 0,
            db1310: fiber.db1310 && !Number.isNaN(fiber.db1310) ? fiber.db1310 : 0,
            db1550: fiber.db1550 && !Number.isNaN(fiber.db1550) ? fiber.db1550 : 0,
          },
        };

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                linkDataArray: [...existingLinks, newLink],
              },
            },
          },
        };
      });
    },
  });

  return {
    data,
    mutate: (input: MutateCreateFiberFiber, opts?: any) =>
      mutate(
        {
          ...input,
          id: input.id ?? generateNodeId(),
        },
        opts,
      ),
    mutateAsync: (input: MutateCreateFiberFiber, opts?: any) =>
      mutateAsync(
        {
          ...input,
          id: input.id ?? generateNodeId(),
        },
        opts,
      ),
    isLoading,
    isError,
    isSuccess,
  };
};
